import { html, LitElement } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { Keys } from '../../../types/common/types';
import { GzDialog, GzDialogEvents } from './GzDialog';
import { WEB_COMPONENT_GZ_DIALOG } from '../../../webcomponents.constants';

export class GzDialogWebComponent extends LitElement implements GzDialog {
  static get styles() {
    return styles;
  }

  @property({ type: String, attribute: true, reflect: true })
  headline = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  isOpen = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  isWindow = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  closableOutsideBox = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  public autoCloseOnConfirm = false;

  @property({ type: Number, attribute: true, reflect: true })
  public overlayZIndex = 9999;

  @state()
  public bodyContent: HTMLElement;

  @state()
  public footerContent?: HTMLElement;

  @query('.focusable')
  private focusableElement: HTMLElement;

  onCloseHandler?: null | Function;

  static buildDialogElement(config: GzDialog, parentElement?: HTMLElement): GzDialogWebComponent {
    // The dialog will be inserted directly into the document body, unless a parent is specified
    const parent = parentElement ? parentElement : document.body;
    const previousDialogInstance = parent.querySelector(WEB_COMPONENT_GZ_DIALOG) as GzDialogWebComponent;

    // If a dialog instance already exists and is not of type window, destroy it
    if (previousDialogInstance && !previousDialogInstance.isWindow) {
      previousDialogInstance.close();
    }

    const dialog = new GzDialogWebComponent();

    for (const prop in config) {
      if (config.hasOwnProperty(prop)) {
        dialog[prop] = config[prop];
      }
    }

    parent.appendChild(dialog);

    if (config.isOpen) {
      dialog.open();
    }

    return dialog;
  }

  public static computeCustomDialog(
    windowBody: string | HTMLElement,
    windowFooterHtml: string,
    windowTitle: string,
    cancelCallback?: null | EventListenerOrEventListenerObject,
    confirmCallback?: null | EventListenerOrEventListenerObject,
    detail?: null | Object,
    autoCloseOnConfirm: boolean = true
  ) {
    let dialogBody;
    if (typeof windowBody === 'string') {
      dialogBody = document.createElement('div');
      dialogBody.innerHTML = windowBody;
    } else {
      dialogBody = windowBody;
    }

    const footer = document.createElement('div');

    footer.innerHTML = windowFooterHtml;

    const dialogConfigs: GzDialog = {
      headline: windowTitle,
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
      autoCloseOnConfirm,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);
    if (cancelCallback) {
      dialog.addEventListener(GzDialogEvents.CANCEL, cancelCallback);
    }

    if (confirmCallback) {
      dialog.addEventListener(GzDialogEvents.CONFIRM, confirmCallback);
    }

    const footerCancelBtns = footer.querySelectorAll('#cancel');
    [].forEach.call(footerCancelBtns, function (footerCancelBtn) {
      footerCancelBtn.addEventListener('click', () => dialog.closeHandlerCallback());
    });

    const footerConfirmBtn = footer.querySelector('#confirm');
    if (footerConfirmBtn) {
      footerConfirmBtn.addEventListener('click', () => {
        if (dialog.autoCloseOnConfirm) {
          dialog.close();
        }

        dialog.emitDialogEvent(GzDialogEvents.CONFIRM, detail);
      });
    }

    return dialog;
  }

  private renderWindowContent() {
    return html`
      <div class="window focusable" tabindex="0">
        <div class="window--content">
          <div class="window--header">
            <gz-title fontSize="22" fontWeight="500" class="window--title">${this.headline}</gz-title>
            <gz-icon-button
              @click="${this.closeHandlerCallback}"
              class="dialog-close"
              icon="ds-icon-clear"
              size="24"
              fontsize="20"
              buttontype="square"
              buttonfill="quiet"
              buttoncolor="secondary"
            >
            </gz-icon-button>
          </div>

          <div class="window--message">${this.bodyContent}</div>
          ${this.footerContent === undefined ? '' : this.renderWindowFooter()}
        </div>
      </div>
    `;
  }

  private renderWindowFooter() {
    return html` <div class="window--footer">${this.footerContent}</div>`;
  }

  private renderDialogContent() {
    return html`
      <div class="dialog focusable" tabindex="0" >
        <div class="dialog--content">
          <div class="dialog--header">
            <gz-icon-button
              @click="${this.closeHandlerCallback}"
              class="dialog-close"
              icon="ds-icon-clear"
              size="24"
              fontsize="20"
              buttontype="square"
              buttonfill="quiet"
              buttoncolor="secondary"
            >
            </gz-icon-button>
            <gz-title fontSize="22" fontWeight="500" class="dialog--title">${this.headline}</h2>
          </div>

          <div class="dialog--message">${this.bodyContent}</div>
          <div class="dialog--footer ${this.footerContent ? '' : 'hidden'}">${this.footerContent}</div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="overlay" @click="${this.handleOverlayClick}" style="z-index:${this.overlayZIndex}">
        ${this.isWindow ? this.renderWindowContent() : this.renderDialogContent()}
      </div>
    `;
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('keyup', e => this.onKeydown(e));
  }

  disconnectedCallback(): void {
    window.removeEventListener('keyup', this.onKeydown);
    super.disconnectedCallback();
  }

  public open() {
    this.isOpen = true;
    // disable page scroll when the dialog is open
    this.addBodyMask();
    this.emitDialogEvent(GzDialogEvents.OPENED, { isWindow: this.isWindow });
    this.updateComplete.then(() => {
      this.focusableElement.focus();
    });
  }

  public close() {
    this.isOpen = false;
    // restore page scroll when the dialog is closed
    this.removeBodyMask();
    if (this.parentNode) {
      this.parentNode.removeChild(this);
    }
  }

  public dismiss() {
    this.emitDialogEvent(GzDialogEvents.CANCEL);
    this.close();
  }

  private closeHandlerCallback() {
    if (!this.isOpen) {
      return;
    }

    if (typeof this.onCloseHandler === 'function') {
      this.onCloseHandler();
    } else {
      this.dismiss();
    }
  }

  public emitDialogEvent(event: GzDialogEvents, detail?: null | Object) {
    const ev = new CustomEvent(event, {
      bubbles: true,
      composed: true,
      detail: detail,
    });
    this.dispatchEvent(ev);
  }

  private onKeydown(event: KeyboardEvent) {
    if (event.key === Keys.Escape) {
      this.closeHandlerCallback();
    }
  }

  private addBodyMask() {
    document.body.style.overflow = 'hidden';
  }

  private removeBodyMask() {
    document.body.style.overflow = 'visible';
  }

  private onOverlayClick(event: Event) {
    /**
     * Close the dialog only when the overlay is clicked
     */
    if (event.target !== event.currentTarget) {
      return;
    }
    this.closeHandlerCallback();
  }

  private handleOverlayClick(event) {
    if (!this.closableOutsideBox) {
      return;
    }

    this.onOverlayClick(event);
  }
}
